import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiErrorMessage } from '@/lib/clients';
import {
  GRANTABLE_PERMISSION_GROUPS, missingPermissions, useCreateCustomRole, useUpdateCustomRole, type CustomRole,
} from '@/lib/custom-roles';
import type { Permission } from '@/lib/permissions';
import { PERMISSION_LABELS } from '@/lib/role-display';
import { cn } from '@/lib/utils';

// Aligné sur createSchema (tenants-service, routes/custom-roles/custom-roles.controller.ts) ; au moins une
// permission en plus : un rôle vide n'a pas d'usage
const schema = z.object({
  name: z.string().trim().min(1, 'Nom requis').max(100, '100 caractères maximum'),
  description: z.string().max(2000, '2000 caractères maximum'),
  permissions: z.array(z.string()).min(1, 'Au moins une permission requise'),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  /** null : création */
  role: CustomRole | null;
  onOpenChange: (open: boolean) => void;
}

export function CustomRoleEditor({ open, role, onOpenChange }: Props) {
  const create = useCreateCustomRole();
  const update = useUpdateCustomRole();
  const mutation = role ? update : create;
  const { register, handleSubmit, reset, formState: { errors } } =
    useForm<FormData>({ resolver: zodResolver(schema), defaultValues: { name: '', description: '', permissions: [] } });

  const resetCreate = create.reset;
  const resetUpdate = update.reset;
  useEffect(() => {
    if (!open) return;
    reset({ name: role?.name ?? '', description: role?.description ?? '', permissions: role?.permissions ?? [] });
    resetCreate();
    resetUpdate();
  }, [open, role, reset, resetCreate, resetUpdate]);

  const blocked = new Set(missingPermissions(mutation.error));

  const submit = (d: FormData) => {
    const body = { name: d.name, description: d.description.trim() || null, permissions: d.permissions as Permission[] };
    const onSuccess = () => {
      toast.success(role ? `Rôle « ${d.name} » mis à jour` : `Rôle « ${d.name} » créé`);
      onOpenChange(false);
    };
    if (role) update.mutate({ id: role.id, ...body }, { onSuccess });
    else create.mutate(body, { onSuccess });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{role ? 'Modifier le rôle' : 'Nouveau rôle personnalisé'}</DialogTitle>
          <DialogDescription>
            Le rôle remplace les permissions du rôle standard des membres qui le portent ; leur rang ne change pas.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(submit)} className="space-y-4">
          <div>
            <Label htmlFor="custom-role-name">Nom *</Label>
            <Input id="custom-role-name" autoComplete="off" {...register('name')} placeholder="Chef de quai" />
            {errors.name && <p className="text-xs text-danger mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <Label htmlFor="custom-role-description">Description</Label>
            <textarea
              autoComplete="off"
              id="custom-role-description"
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              {...register('description')}
            />
            {errors.description && <p className="text-xs text-danger mt-1">{errors.description.message}</p>}
          </div>
          <fieldset>
            <legend className="text-sm font-medium">Permissions *</legend>
            <div className="mt-2 max-h-[45vh] overflow-y-auto rounded-md border p-3 grid gap-4 sm:grid-cols-2">
              {GRANTABLE_PERMISSION_GROUPS.map((g) => (
                <div key={g.label}>
                  <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">{g.label}</p>
                  {g.permissions.map((p) => (
                    <label key={p} className={cn('flex items-center gap-2 text-sm py-0.5', blocked.has(p) && 'text-danger font-medium')}>
                      <input type="checkbox" value={p} {...register('permissions')} />
                      {PERMISSION_LABELS[p]}
                    </label>
                  ))}
                </div>
              ))}
            </div>
            {errors.permissions && <p className="text-xs text-danger mt-1">{errors.permissions.message}</p>}
          </fieldset>
          {mutation.isError && (
            <p role="alert" className="text-sm text-danger">
              {blocked.size
                ? 'Vous ne pouvez pas accorder ces permissions car vous ne les possédez pas vous-même (en rouge ci-dessus).'
                : apiErrorMessage(mutation.error, 'Erreur lors de l’enregistrement')}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Enregistrement…' : role ? 'Enregistrer' : 'Créer le rôle'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
